import { ApolloError } from '@apollo/client'

import { useDevicesWithTailgateConfiguredQuery } from '@/graphql/generated/hooks'

import { DeviceConfigurationT } from '../types/types'
import { getDeviceConfigurations } from '../utils/utils'

interface UseMisconfiguredDoorsIProps {
  /** Door device ids to check; no request is made while empty. */
  doorIds: string[]
}

interface UseMisconfiguredDoorsResultIProps {
  /** Doors missing a tailgate-grid association or configuration. */
  misconfiguredDoors: DeviceConfigurationT[]
  /** True once the check has a settled result for the given doorIds. */
  hasDoorsResult: boolean
  isDoorsLoading: boolean
  doorsError?: ApolloError
  refetchDoors: () => void
}

/**
 * Tailgate-configuration check for a set of door devices, shared by the
 * legacy criteria section (always-on, driven by the rule's device pickers)
 * and the v2 on-demand door check.
 */
export const useMisconfiguredDoors = ({
  doorIds,
}: UseMisconfiguredDoorsIProps): UseMisconfiguredDoorsResultIProps => {
  const {
    data: doorsData,
    loading: isDoorsLoading,
    error: doorsError,
    refetch,
  } = useDevicesWithTailgateConfiguredQuery({
    variables: { filter: { idIn: doorIds } },
    skip: doorIds.length === 0,
    // A check must report live door state: with the default cache-first
    // policy, re-checking an unchanged scope (same ids, same variables)
    // would serve the previous result even after a door was fixed elsewhere.
    fetchPolicy: 'network-only',
  })

  const hasDoorsResult =
    doorIds.length > 0 && !isDoorsLoading && !doorsError && !!doorsData

  const misconfiguredDoors = hasDoorsResult
    ? getDeviceConfigurations(doorsData)
    : []

  return {
    misconfiguredDoors,
    hasDoorsResult,
    isDoorsLoading,
    doorsError,
    // Swallowed rejection: the hook's own error state reports refetch failures
    refetchDoors: () => {
      refetch().catch(() => undefined)
    },
  }
}
